import { useState, useEffect, useCallback } from "react";

interface DeviceAndWalletState {
	isMobile: boolean;
	hasPhantomExtension: boolean;
}

/**
 * Hook to detect device type and wallet extension availability
 * Improved with proper async detection for browser extensions
 */
export function useDeviceAndWalletDetection(): DeviceAndWalletState {
	const [isMobile, setIsMobile] = useState(false);
	const [hasPhantomExtension, setHasPhantomExtension] = useState(false);

	// Detect mobile device
	const checkMobile = useCallback((): boolean => {
		return (
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent
			) || window.innerWidth <= 768
		);
	}, []);

	// Robust Phantom extension detection
	const checkPhantomExtension = useCallback((): boolean => {
		if (checkMobile() || typeof window === "undefined") {
			return false;
		}

		const solana = (window as any).solana;
		const isPhantomAvailable = !!(solana && solana.isPhantom);

		return isPhantomAvailable;
	}, [checkMobile]);

	const updatePhantomState = useCallback(() => {
		if (!checkMobile()) {
			const hasPhantom = checkPhantomExtension();
			if (hasPhantom) {
				setHasPhantomExtension(true);
			}
		}
	}, [checkMobile, checkPhantomExtension]);

	const updateMobileState = useCallback(() => {
		const mobile = checkMobile();
		setIsMobile(mobile);
		if (mobile) {
			setHasPhantomExtension(false);
		}
	}, [checkMobile]);

	useEffect(() => {
		// Initial state updates
		updateMobileState();
		updatePhantomState();

		// Handle window resize
		const handleResize = () => {
			updateMobileState();
			updatePhantomState();
		};

		// Listen for Phantom's ready event (if available)
		const handlePhantomReady = () => {
			updatePhantomState();
		};

		// Retry mechanism for phantom detection
		const maxRetries = 10;
		let retryCount = 0;
		const retryIntervals: ReturnType<typeof setTimeout>[] = [];

		const retryPhantomDetection = () => {
			if (retryCount >= maxRetries || checkMobile()) {
				return;
			}

			const hasPhantom = checkPhantomExtension();
			if (hasPhantom) {
				setHasPhantomExtension(true);
				// Clear remaining retries
				retryIntervals.forEach(clearTimeout);
				return;
			}

			retryCount++;
			// Exponential backoff: 500ms, 1s, 2s, 4s, then 5s intervals
			const delay =
				retryCount <= 4
					? Math.min(500 * Math.pow(2, retryCount - 1), 4000)
					: 5000;

			const timeoutId = setTimeout(retryPhantomDetection, delay);
			retryIntervals.push(timeoutId);
		};

		// Start retry mechanism after initial check
		if (!checkMobile() && !checkPhantomExtension()) {
			const initialRetryTimeout = setTimeout(retryPhantomDetection, 500);
			retryIntervals.push(initialRetryTimeout);
		}

		// Event listeners
		window.addEventListener("resize", handleResize);

		// Listen for various Phantom events
		window.addEventListener("solana#initialized", handlePhantomReady);
		window.addEventListener("phantom#initialized", handlePhantomReady);

		// Generic wallet events
		document.addEventListener("DOMContentLoaded", updatePhantomState);
		window.addEventListener("load", updatePhantomState);

		// Cleanup function
		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("solana#initialized", handlePhantomReady);
			window.removeEventListener("phantom#initialized", handlePhantomReady);
			document.removeEventListener("DOMContentLoaded", updatePhantomState);
			window.removeEventListener("load", updatePhantomState);

			// Clear all retry timeouts
			retryIntervals.forEach(clearTimeout);
		};
	}, [
		updateMobileState,
		updatePhantomState,
		checkMobile,
		checkPhantomExtension,
	]);

	return { isMobile, hasPhantomExtension };
}
